import { useState, useEffect, useCallback } from 'react';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GridCellParams, GridColDef, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getInvoiceRemindersSelection } from 'src/shared/services/invoiceService';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clear, SimCardDownload, Email } from '@mui/icons-material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { downloadInvoicePdf } from 'src/shared/functions/downloadInvoicePdf';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { formatDate } from 'src/shared/functions/formatDate';
import { Colors } from 'src/shared/components/styles/colors';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { InvoiceRemindersSelection } from '../models/invoiceRemindersSelection';
import { Invoice } from '../models/invoice';
import { InvoiceStatuses } from './invoiceStatuses';
import HandleInvoicesButton from './handleInvoicesButton';

type InvoiceReminderDataGridProps = {
  searchFilter: InvoiceRemindersSelection;
  loadingCallback: (loading: boolean) => void;
  hideCheckBoxSelection?: boolean;
  setTotalInvoices?: (totalItems: number) => void;
};

export const InvoiceReminderDataGrid = (props: InvoiceReminderDataGridProps) => {
  const { searchFilter, loadingCallback, hideCheckBoxSelection, setTotalInvoices } = props;
  const { t } = useTranslation(['member', 'invoice', 'common']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [loadingPDF, setLoadingPDF] = useState<boolean>(false);
  const [rows, setRows] = useState<Invoice[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'dueDate', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }
    try {
      const result = await getInvoiceRemindersSelection(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter);

      const invoiceResult = extendObjectWithId(result.data.results, 'invoice_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        if (setTotalInvoices) {
          setTotalInvoices(result.data.totalItems);
        }
        ConsoleHelper.log(invoiceResult, 'invoiceResult');

        setTimeout(() => {
          setRows(invoiceResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        loadingCallback(false);
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, isMounted, loadingCallback, searchFilter, setTotalInvoices, sortModel]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [data.page, data.pageSize, sortModel, fetchData, isMounted]);

  useEffect(() => {
    if (data.page !== 0) {
      updateData('page', 0);
    }
  }, [data.page, searchFilter]);

  const pdfLoadingCallback = useCallback((isLoading: boolean) => {
    setLoadingPDF(isLoading);
  }, []);

  const downloadPdf = async (id) => {
    downloadInvoicePdf(id, pdfLoadingCallback);
  };

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.InvoicesDetail}/${params.row.id}`);
  };

  const columns: GridColDef[] = [];
  columns.push(
    {
      field: 'ocr',
      headerName: t('invoice:OCR'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.ocr}</>;
      },
    },
    {
      field: 'member',
      headerName: t('invoice:Member'),
      flex: 1.5,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        return params.row.member !== null ? (
          <>
            {params.row.member.firstName ?? ''} {params.row.member.lastName ?? ''}
          </>
        ) : null;
      },
    },
    {
      field: 'category',
      headerName: t('invoice:InvoiceType'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return params.row.category !== null ? <>{params.row.category.text}</> : null;
      },
    },
    {
      field: 'amount',
      headerName: t('invoice:amount'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const { amount } = params.row;
        return (amount && <AmountWithCurrency amount={amount} />) || '-';
      },
    },
    {
      field: 'dueDate',
      headerName: t('invoice:dueDate'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { dueDate } = params.row;
        return formatDate(dueDate);
      },
    },
    {
      field: 'status',
      headerName: t('invoice:invoiceStatusLabel'),
      sortable: false,
      width: 100,
      renderCell: (params: GridCellParams) => {
        const { status: stat } = params.row;
        return <InvoiceStatuses status={stat} chipSize='small' />;
      },
    },
    {
      field: 'isSent',
      headerName: '', // t('invoice:sent'),
      sortable: false,
      width: 40,
      renderCell: (params: GridCellParams<{ isSent: boolean | null | undefined }>) => {
        const isSent = params.row.isSent as boolean;
        return (
          <Tooltip title={isSent ? t('invoice:sent') : t('invoice:unSent')} arrow>
            <span>{isSent ? <Email fontSize='small' sx={{ color: 'grey' }} /> : null}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'invoice_id',
      headerName: '',
      width: 40,
      renderCell: (params: GridCellParams) => {
        return (
          <Tooltip title={t('invoice:DownloadAsPDF')}>
            <span>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  downloadPdf(params.row.invoice_id);
                }}
                style={{ display: 'flex', color: Colors.LinkColor }}
                size='small'
              >
                <SimCardDownload />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  );

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <LoadingOverlay message={t('invoice:loadingPdf')} loading={loadingPDF} />
      <DataGrid<Invoice>
        autoHeight
        autoRowHeight={false}
        columns={columns}
        rows={rows}
        dataModel={data}
        onRowClick={onRowClick}
        disableSelectionOnClick
        checkboxSelection={!hideCheckBoxSelection}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <HandleInvoicesButton rows={rows} selectionModel={selectionModel} fetchData={fetchData} setSelectionModel={setSelectionModel} />
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};
